import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { AlertTriangle, Download, Loader2, RefreshCw } from "lucide-react";
import {
  AjusteItemInvoice,
  MudancaInvoice,
  baixarInvoiceAdmin,
  reemitirInvoiceAjustada,
} from "services/admin/adminEnvios.service";

/*
 * Correção da invoice depois de emitida.
 *
 * Campo em branco quer dizer "não mexer", e não "zerar": o placeholder
 * mostra o valor que está valendo hoje, e só o que for digitado vira
 * ajuste. Isso espelha a regra do backend e evita que abrir o formulário e
 * salvar sem querer apague descrição de item.
 *
 * Os ajustes não entram em `pedido`: o preço do item também alimenta a
 * estimativa de imposto, a cotação das transportadoras e o CPSC. Aqui se
 * corrige o papel, não a cotação.
 */

type Props = {
  envio: any | null;
  onFechar: () => void;
  onReemitido: () => void;
};

// Mesmos apelidos que o gerador da invoice consulta, na mesma ordem.
const precoAtual = (it: any) => Number(it?.preco ?? it?.unitPrice ?? it?.price) || 0;
const qtyAtual = (it: any) => Number(it?.qty ?? it?.quantidade) || 1;
const descricaoAtual = (it: any) =>
  String(it?.descricao ?? it?.description ?? it?.titulo ?? it?.name ?? "").trim();

const moeda = (v: number, cur: string) =>
  v.toLocaleString("en-US", { style: "currency", currency: cur || "USD" });

type Rascunho = { preco: string; qty: string; descricao: string };

const vazio = (): Rascunho => ({ preco: "", qty: "", descricao: "" });

const InvoiceAjuste = ({ envio, onFechar, onReemitido }: Props) => {
  const itens: any[] = useMemo(
    () => (Array.isArray(envio?.pedido?.itens) ? envio.pedido.itens : []),
    [envio]
  );
  const cur = String(envio?.pedido?.moeda || "USD").toUpperCase();

  const [rascunhos, setRascunhos] = useState<Rascunho[]>([]);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<MudancaInvoice[] | null>(null);

  // Trocar de envio com o modal aberto não pode carregar o rascunho do
  // anterior: seria corrigir a invoice de um envio com o valor de outro.
  useEffect(() => {
    setRascunhos(itens.map(vazio));
    setMotivo("");
    setErro(null);
    setFeito(null);
  }, [envio?.id, itens]);

  const alterar = (i: number, campo: keyof Rascunho, valor: string) =>
    setRascunhos((prev) => prev.map((r, n) => (n === i ? { ...r, [campo]: valor } : r)));

  // O que de fato mudou, para o resumo e para habilitar o botão.
  const ajustes: AjusteItemInvoice[] = useMemo(() => {
    const saida: AjusteItemInvoice[] = [];
    rascunhos.forEach((r, i) => {
      const item = itens[i];
      if (!item) return;
      const a: AjusteItemInvoice = { indice: i };
      let mexeu = false;

      if (r.preco.trim() && Number(r.preco) !== precoAtual(item)) {
        a.preco = r.preco.trim();
        mexeu = true;
      }
      if (r.qty.trim() && Number(r.qty) !== qtyAtual(item)) {
        a.qty = r.qty.trim();
        mexeu = true;
      }
      if (r.descricao.trim() && r.descricao.trim() !== descricaoAtual(item)) {
        a.descricao = r.descricao.trim();
        mexeu = true;
      }
      if (mexeu) saida.push(a);
    });
    return saida;
  }, [rascunhos, itens]);

  const totalAtual = itens.reduce((s, it) => s + precoAtual(it) * qtyAtual(it), 0);
  const totalNovo = itens.reduce((s, it, i) => {
    const r = rascunhos[i];
    const p = r?.preco.trim() ? Number(r.preco) : precoAtual(it);
    const q = r?.qty.trim() ? Number(r.qty) : qtyAtual(it);
    return s + (Number.isFinite(p) ? p : 0) * (Number.isFinite(q) ? q : 0);
  }, 0);

  const baixar = async () => {
    setBaixando(true);
    setErro(null);
    try {
      const blob = await baixarInvoiceAdmin(envio.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${envio.pedido_ref || "pedido"}-IN.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setErro(err?.response?.data?.error || "Não foi possível baixar a invoice.");
    } finally {
      setBaixando(false);
    }
  };

  const reemitir = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const resp = await reemitirInvoiceAjustada(envio.id, { itens: ajustes, motivo });
      setFeito(resp.mudancas || []);
      onReemitido();
    } catch (err: any) {
      setErro(err?.response?.data?.error || "Não foi possível reemitir a invoice.");
    } finally {
      setEnviando(false);
    }
  };

  if (!envio) return null;

  const temInvoice = Boolean(envio.invoice_path);
  const origem = envio.invoice_origem as string | null;
  const jaCorrigida = envio.invoice_ajustes;

  return (
    <Dialog open={Boolean(envio)} onOpenChange={(aberto) => !aberto && onFechar()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Corrigir invoice · {envio.pedido_ref}</DialogTitle>
          <DialogDescription>
            {envio.cliente?.razaoSocial || `Cliente ${envio.cliente_id}`}
            {envio.tracking_number ? ` · ${envio.tracking_number}` : ""}
          </DialogDescription>
        </DialogHeader>

        {!temInvoice && (
          <p className="rounded-md bg-slate-100 p-3 text-xs text-slate-600">
            Esta cotação ainda não tem invoice salva. Reemitir vai gerar a
            primeira, com os valores corrigidos.
          </p>
        )}

        {/* A invoice da transportadora é a que foi transmitida à alfândega na
            emissão. Substituir é possível, mas quem faz precisa saber. */}
        {temInvoice && origem !== "INTREX" && (
          <div className="flex gap-2 rounded-md bg-amber-50 p-3 ring-1 ring-inset ring-amber-600/15">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-snug text-amber-800">
              A invoice que está salva não foi emitida pela Intrex — veio da
              transportadora ou foi anexada à mão. Se a transportadora
              transmitiu os valores no despacho, a invoice nova passa a
              divergir do que a alfândega recebeu.
            </p>
          </div>
        )}

        {jaCorrigida && (
          <p className="text-xs text-slate-500">
            Já corrigida em{" "}
            {new Date(jaCorrigida.editado_em).toLocaleString("pt-BR")}
            {jaCorrigida.editado_por?.nome ? ` por ${jaCorrigida.editado_por.nome}` : ""}
            {jaCorrigida.motivo ? ` — "${jaCorrigida.motivo}"` : ""}.
          </p>
        )}

        {feito ? (
          <div className="rounded-md bg-green-50 p-3 ring-1 ring-inset ring-green-600/15">
            <p className="text-sm font-medium text-green-800">Invoice reemitida.</p>
            <ul className="mt-1 space-y-0.5 text-xs text-green-700">
              {feito.map((m, i) => (
                <li key={i}>
                  Item {m.indice + 1} · {m.campo}: {String(m.de ?? "—")} → {String(m.para)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="pb-2 pr-2 font-medium">#</th>
                    <th className="pb-2 pr-2 font-medium">Descrição na invoice</th>
                    <th className="pb-2 pr-2 font-medium w-20">Qtd</th>
                    <th className="pb-2 font-medium w-28">Valor unit.</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100 align-top">
                      <td className="py-2 pr-2 text-slate-400">{i + 1}</td>
                      <td className="py-2 pr-2">
                        <Input
                          value={rascunhos[i]?.descricao ?? ""}
                          placeholder={descricaoAtual(it) || "—"}
                          onChange={(e) => alterar(i, "descricao", e.target.value)}
                          className="h-8 text-xs"
                        />
                        {it.sku && (
                          <span className="mt-0.5 block text-[10px] text-slate-400">
                            SKU {it.sku}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2">
                        <Input
                          value={rascunhos[i]?.qty ?? ""}
                          placeholder={String(qtyAtual(it))}
                          inputMode="numeric"
                          onChange={(e) => alterar(i, "qty", e.target.value)}
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="py-2">
                        <Input
                          value={rascunhos[i]?.preco ?? ""}
                          placeholder={precoAtual(it).toFixed(2)}
                          inputMode="decimal"
                          onChange={(e) => alterar(i, "preco", e.target.value)}
                          className="h-8 text-xs"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-slate-500">
              Campo em branco fica como está. O pedido e a estimativa de
              imposto não mudam — a correção vale só na invoice.
            </p>

            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs">
              <span className="text-slate-500">Total das mercadorias</span>
              <span className="font-medium text-slate-700">
                {moeda(totalAtual, cur)}
                {totalNovo !== totalAtual && (
                  <>
                    {" → "}
                    <span className="text-slate-900">{moeda(totalNovo, cur)}</span>
                  </>
                )}
              </span>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700" htmlFor="motivo-invoice">
                Motivo da correção
              </label>
              <Input
                id="motivo-invoice"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ex.: valor unitário digitado errado no cadastro"
                className="mt-1 h-9 text-xs"
              />
              <p className="mt-1 text-[10px] text-slate-400">
                Fica no log de auditoria junto do de/para de cada campo.
              </p>
            </div>
          </>
        )}

        {erro && (
          <p className="rounded-md bg-red-50 p-2 text-xs text-red-700 ring-1 ring-inset ring-red-600/15">
            {erro}
          </p>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={baixar}
            disabled={!temInvoice || baixando}
          >
            {baixando ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-3.5 w-3.5" />
            )}
            Baixar a invoice atual
          </Button>

          {feito ? (
            <Button size="sm" onClick={onFechar}>
              Fechar
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={reemitir}
              disabled={enviando || !ajustes.length || motivo.trim().length < 3}
              title={
                !ajustes.length
                  ? "Altere pelo menos um campo"
                  : motivo.trim().length < 3
                    ? "Informe o motivo"
                    : "Gera a invoice nova"
              }
            >
              {enviando ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              )}
              {enviando ? "Reemitindo..." : "Reemitir invoice"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceAjuste;
